import { useState } from "react";
import { Loader } from "../components/Loader.tsx";
import { authClient, signOut, useIsOwner, useSession } from "../lib/auth.ts";
import type { ApiKey } from "../lib/types.ts";
import { useAsync } from "../lib/useAsync.ts";

function formatDate(iso: string | null): string {
  if (!iso) return "nunca";
  return new Intl.DateTimeFormat("es-ES", {
    dateStyle: "medium",
    timeZone: "Europe/Madrid",
  }).format(new Date(iso));
}

/**
 * Solo se monta para el rol `owner`: el backend responde 403 a un `viewer` en
 * /api/auth/api-key/*, asi que ni siquiera conviene pedir el listado.
 */
function ApiKeys() {
  const [name, setName] = useState("");
  const [created, setCreated] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const { data: keys, error: loadError, loading, reload } = useAsync<ApiKey[]>(
    async () => {
      const { data, error: listError } = await authClient.apiKey.list();
      if (listError) throw new Error(listError.message ?? "No se pudieron cargar");
      // El endpoint responde { apiKeys: [...] }, no un array pelado.
      const payload = data as unknown as { apiKeys?: ApiKey[] } | ApiKey[] | null;
      if (Array.isArray(payload)) return payload;
      return payload?.apiKeys ?? [];
    },
    [],
  );

  async function createKey(): Promise<void> {
    if (!name.trim()) return;
    setBusy(true);
    setError(null);
    setCopied(false);

    const { data, error: createError } = await authClient.apiKey.create({
      name: name.trim(),
    });

    if (createError || !data) {
      setError(createError?.message ?? "No se pudo crear la clave");
    } else {
      setCreated(data.key);
      setName("");
      reload();
    }
    setBusy(false);
  }

  async function removeKey(keyId: string): Promise<void> {
    const { error: deleteError } = await authClient.apiKey.delete({ keyId });
    if (deleteError) setError(deleteError.message ?? "No se pudo revocar");
    else reload();
  }

  return (
    <>
      <section className="card">
        <div className="card__head">
          <h2>Claves de API</h2>
          <span className="card__hint">Para el ESP32, agentes y MCP</span>
        </div>

        <p className="subtle" style={{ marginBottom: 14 }}>
          Cada clave se envía en la cabecera <code>x-api-key</code>. Ponle un
          nombre reconocible: queda guardado en cada registro que cree, para que
          siempre sepas de dónde vino.
        </p>

        {error && (
          <div className="alert alert--error" role="alert" style={{ marginBottom: 12 }}>
            {error}
          </div>
        )}

        {created && (
          <div className="alert alert--success" style={{ marginBottom: 14 }}>
            <p style={{ fontWeight: 600, marginBottom: 6 }}>
              Cópiala ahora: no se vuelve a mostrar.
            </p>
            <code className="keyvalue">{created}</code>
            <div className="row" style={{ marginTop: 8 }}>
              <button
                className="btn btn--quiet"
                onClick={() => {
                  void navigator.clipboard.writeText(created);
                  setCopied(true);
                }}
              >
                {copied ? "Copiada" : "Copiar"}
              </button>
              <button className="btn btn--quiet" onClick={() => setCreated(null)}>
                Ocultar
              </button>
            </div>
          </div>
        )}

        <div className="row" style={{ marginBottom: 16 }}>
          <input
            className="input"
            placeholder="Nombre (p. ej. ESP32 del salón)"
            value={name}
            maxLength={60}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") void createKey();
            }}
          />
          <button className="btn" onClick={() => void createKey()} disabled={busy || !name.trim()}>
            Crear
          </button>
        </div>

        <Loader loading={loading} error={loadError}>
          {keys && keys.length === 0 && (
            <p className="empty">Todavía no hay ninguna clave.</p>
          )}
          {keys && keys.length > 0 && (
            <ul className="entries">
              {keys.map((key) => (
                <li className="entry" key={key.id}>
                  <div className="entry__body">
                    <div className="entry__head">
                      <strong>{key.name ?? "(sin nombre)"}</strong>
                      <code className="subtle">{key.start}…</code>
                      <span className="spacer" />
                      <button
                        className="btn btn--quiet btn--danger"
                        onClick={() => void removeKey(key.id)}
                      >
                        Revocar
                      </button>
                    </div>
                    <div className="entry__meta">
                      <span className="subtle">
                        Creada el {formatDate(key.createdAt)} · {key.requestCount}{" "}
                        peticiones · último uso: {formatDate(key.lastRequest)}
                      </span>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Loader>
      </section>

      <section className="card">
        <div className="card__head">
          <h2>Conectar un agente</h2>
        </div>
        <p className="subtle">
          El servidor MCP vive en <code>/mcp</code> y acepta la misma cabecera{" "}
          <code>x-api-key</code>. Tools disponibles: <code>log_entry</code>,{" "}
          <code>get_today_status</code>, <code>list_entries</code>,{" "}
          <code>get_stats</code> y <code>list_moods</code>.
        </p>
      </section>
    </>
  );
}

export function Settings() {
  const { data: session } = useSession();
  const isOwner = useIsOwner();

  return (
    <>
      <section className="card">
        <div className="card__head">
          <h2>Cuenta</h2>
        </div>
        <div className="row">
          <div>
            <strong>{session?.user.email}</strong>
            <p className="subtle">
              {isOwner ? "Sesión iniciada" : "Cuenta de solo lectura"}
            </p>
          </div>
          <span className="spacer" />
          <button className="btn btn--ghost" onClick={() => void signOut()}>
            Cerrar sesión
          </button>
        </div>
        {!isOwner && (
          <p className="subtle" style={{ marginTop: 12 }}>
            Puedes consultar los registros y la evolución, pero no crear,
            editar ni borrar nada.
          </p>
        )}
      </section>

      {isOwner && <ApiKeys />}
    </>
  );
}
